// product-form.js — quiet + auto-init when a <form> appears (no warnings)
if (!customElements.get('product-form')) {
  customElements.define(
    'product-form',
    class ProductForm extends HTMLElement {
      constructor() {
        super();
        this._inited = false;
        this._observer = null;
        this.onSubmitHandler = this.onSubmitHandler?.bind(this);
      }

      connectedCallback() {
        // Try immediately, then watch for lazy-inserted form content (modals/quick-add)
        queueMicrotask(() => {
          if (!this._inited) {
            this.form = this._findForm();
            if (this.form) this._init();
            if (!this._inited) this._observeForForm();
          }
        });
      }

      disconnectedCallback() {
        this._teardown();
      }

      _findForm() {
        return (
          this.querySelector('form[action*="/cart/add"]') ||
          this.querySelector('form') ||
          (this.matches('form') ? this : null)
        );
      }

      _observeForForm() {
        if (this._observer) return;
        this._observer = new MutationObserver(() => {
          if (this._inited) return;
          const f = this._findForm();
          if (f) {
            this.form = f;
            this._init();
            this._disconnectObserver();
          }
        });
        this._observer.observe(this, { childList: true, subtree: true });
      }

      _disconnectObserver() {
        if (this._observer) {
          this._observer.disconnect();
          this._observer = null;
        }
      }

      _init() {
        if (this._inited || !this.form) return;

        const variantInput = this.form.querySelector('[name="id"]');
        const submitBtn = this.querySelector('[type="submit"]');

        if (!variantInput || !submitBtn) {
          // No noise—just no-op for decorative/product-form shells.
          return;
        }

        variantInput.disabled = false;

        this.submitButton = submitBtn;
        this.submitButtonText = this.submitButton.querySelector('span') || this.submitButton;
        this.cart = document.querySelector('cart-notification') || document.querySelector('cart-drawer');
        if (document.querySelector('cart-drawer')) {
          this.submitButton.setAttribute('aria-haspopup', 'dialog');
        }

        this.hideErrors = this.dataset.hideErrors === 'true';

        this.onSubmitHandler = this.onSubmitHandler.bind(this);
        this.form.addEventListener('submit', this.onSubmitHandler);

        this._inited = true;
      }

      _teardown() {
        this._disconnectObserver();
        if (this.form && this.onSubmitHandler) {
          this.form.removeEventListener('submit', this.onSubmitHandler);
        }
        this._inited = false;
      }

      onSubmitHandler(evt) {
        evt.preventDefault();
        if (this.submitButton.getAttribute('aria-disabled') === 'true') return;

        this.handleErrorMessage();

        this.submitButton.setAttribute('aria-disabled', true);
        this.submitButton.classList.add('loading');
        const spinner = this.querySelector('.loading__spinner');
        if (spinner) spinner.classList.remove('hidden');

        const config = fetchConfig('javascript');
        config.headers['X-Requested-With'] = 'XMLHttpRequest';
        delete config.headers['Content-Type'];

        const formData = new FormData(this.form);

        if (this.cart) {
          formData.append(
            'sections',
            this.cart.getSectionsToRender().map((section) => section.id)
          );
          formData.append('sections_url', window.location.pathname);
          this.cart.setActiveElement(document.activeElement);
        }
        config.body = formData;

        fetch(`${routes.cart_add_url}`, config)
          .then((r) => r.json())
          .then((response) => {
            if (response.status) {
              publish(PUB_SUB_EVENTS.cartError, {
                source: 'product-form',
                productVariantId: formData.get('id'),
                errors: response.errors || response.description,
                message: response.message,
              });
              this.handleErrorMessage(response.description);

              const soldOutMessage = this.submitButton.querySelector('.sold-out-message');
              if (!soldOutMessage) return;
              this.submitButton.setAttribute('aria-disabled', true);
              this.submitButtonText.classList.add('hidden');
              soldOutMessage.classList.remove('hidden');
              this.error = true;
              return;
            } else if (!this.cart) {
              window.location = window.routes.cart_url;
              return;
            }

            const startMarker = CartPerformance.createStartingMarker('add:wait-for-subscribers');
            if (!this.error)
              publish(PUB_SUB_EVENTS.cartUpdate, {
                source: 'product-form',
                productVariantId: formData.get('id'),
                cartData: response,
              }).then(() => {
                CartPerformance.measureFromMarker('add:wait-for-subscribers', startMarker);
              });

            this.error = false;

            const quickAddModal = this.closest('quick-add-modal');
            if (quickAddModal) {
              document.body.addEventListener(
                'modalClosed',
                () => {
                  setTimeout(() => {
                    CartPerformance.measure('add:paint-updated-sections', () => {
                      this.cart.renderContents(response);
                    });
                  });
                },
                { once: true }
              );
              quickAddModal.hide(true);
            } else {
              CartPerformance.measure('add:paint-updated-sections', () => {
                this.cart.renderContents(response);
              });
            }
          })
          .catch(console.error)
          .finally(() => {
            this.submitButton.classList.remove('loading');
            if (this.cart && this.cart.classList.contains('is-empty')) this.cart.classList.remove('is-empty');
            if (!this.error) this.submitButton.removeAttribute('aria-disabled');
            const spinner = this.querySelector('.loading__spinner');
            if (spinner) spinner.classList.add('hidden');
            CartPerformance.measureFromEvent('add:user-action', evt);
          });
      }

      handleErrorMessage(errorMessage = false) {
        if (this.hideErrors) return;
        this.errorMessageWrapper =
          this.errorMessageWrapper || this.querySelector('.product-form__error-message-wrapper');
        if (!this.errorMessageWrapper) return;
        this.errorMessage =
          this.errorMessage || this.errorMessageWrapper.querySelector('.product-form__error-message');

        this.errorMessageWrapper.toggleAttribute('hidden', !errorMessage);
        if (errorMessage && this.errorMessage) {
          this.errorMessage.textContent = errorMessage;
        }
      }

      toggleSubmitButton(disable = true, text) {
        if (disable) {
          this.submitButton.setAttribute('disabled', 'disabled');
          if (text) this.submitButtonText.textContent = text;
        } else {
          this.submitButton.removeAttribute('disabled');
          this.submitButtonText.textContent = window.variantStrings.addToCart;
        }
      }

      get variantIdInput() {
        return this.form ? this.form.querySelector('[name="id"]') : null;
      }
    }
  );
}
